/*
 * Jump to a date (X-A4 N12). The day divider *is* the control: the pill a reader is already looking
 * at when they want to go back opens a small panel — today, yesterday, last week, last month, a
 * date field, and the beginning of the conversation — and the chord of `shortcuts.ts` opens the same
 * panel on the pill that is stuck at the top of the list.
 *
 * A date has to become a seq, because history is anchored on seqs and on nothing else
 * (`service_history.go`): `/c/<slug>/p/<seq>` and its direct twin are the only ways into the middle
 * of a conversation. The one thing that already indexes every message by the day it was written is
 * search, so that is what resolves a date — `in:#slug after:… before:…`, newest first, the oldest
 * hit of the window — and the jump costs no new route, no new protocol field and no new action.
 *
 * Three rules keep that honest and cheap:
 *   - the window is bounded and widens only when it found nothing (`WINDOWS`), so "take me to last
 *     Tuesday" is one request rather than a walk through everything written since that Tuesday;
 *   - the hits are filtered by this conversation *in the client*, because a direct conversation has
 *     no slug for `in:` to name — the filter is the reader's own model of which scope a hit is in,
 *     and a hit that arrives is one they may read (`search.Result`);
 *   - and they are filtered by the day's own floor, because `after:`/`before:` are UTC day
 *     boundaries while the day under the pill is the reader's own: the seq has to be the first
 *     message *they* would see under that day.
 */

import { useCallback, useRef, useState, type RefObject } from "react"
import { formatDay, isoDateTime } from "../../../core/i18n/format.ts"
import { useNow } from "../../../core/i18n/useNow.ts"
import { search, searchIsUnavailable } from "../../../core/search/api.ts"
import { isDay, timeAsDay } from "../../../core/search/queryEdit.ts"
import { MAIN_ID } from "../../../core/shell/index.ts"
import type { ConversationId } from "../../../core/store/model.ts"
import { client } from "../../../core/store/store.ts"
import { useAsyncAction } from "../../../lib/useAsyncAction.ts"
import { messageHref } from "../../../routes/links.ts"
import { Button, Field, FormAlert, Popover } from "../../../ui/index.ts"
import { t } from "../i18n/t.ts"
import { navigateTo } from "../navigation.ts"
import { chatSlice } from "../state/selectors.ts"
import "./list.css"

const DAY_MS = 86_400_000

/**
 * How far past the day asked for one search looks, in days, in order; `0` is "no ceiling at all".
 * A quiet week simply moves to the next entry, which costs one more request and never more.
 */
const WINDOWS = [1, 7, 31, 366, 0] as const

/**
 * How many pages of one window are walked before the oldest hit found so far is taken as the
 * answer. It is a ceiling on the cost and not on the correctness of the ordinary case: it is
 * reached only by a day carrying more than `MAX_PAGES * PAGE` messages, and the landing is then
 * inside the day asked for rather than exactly at its first message.
 */
const MAX_PAGES = 8

/** How many hits one page asks for: the route's own maximum (`internal/core/search/doc.go`). */
const PAGE = 50

/**
 * The presets, in the order they are offered. `back` is how many days before today they mean, and the
 * first two are labelled by the same `formatDay` that labels the day pill above them — the panel and
 * the pill a reader opened it from say "Today" in one voice, and the words exist once (§8 P12).
 */
const PRESETS: readonly { id: string; label: (now: number) => string; back: number }[] = [
	{ id: "today", label: (now) => formatDay(now, now), back: 0 },
	{ id: "yesterday", label: (now) => formatDay(now - DAY_MS, now), back: 1 },
	{ id: "lastWeek", label: () => t("chat.jump.lastWeek"), back: 7 },
	{ id: "lastMonth", label: () => t("chat.jump.lastMonth"), back: 30 },
]

/**
 * The midnight that starts a `YYYY-MM-DD` here. `queryEdit.ts` is where the product already turns a
 * day into a moment and back for the search filters, and this is the same clock — so a date typed
 * into the panel and the same date typed into the search box mean one day and not two.
 */
function dayFloorMs(day: string): number {
	const [year, month, date] = day.split("-").map(Number)
	return new Date(year ?? 1970, (month ?? 1) - 1, date ?? 1).getTime()
}

/**
 * The query one window asks. `after:` is exclusive of the day it names (`parse.go`), so the floor is
 * named as the day *before* the one wanted; `before:` is exclusive of its own midnight, so the
 * ceiling is the first day past the window. A slug is added where there is one: it makes the server
 * do the filtering that the client would otherwise do over far more pages.
 */
export function jumpQuery(floorMs: number, windowDays: number, slug: string | undefined): string {
	const parts = [`after:${timeAsDay(floorMs - DAY_MS)}`]
	if (windowDays > 0) parts.push(`before:${timeAsDay(floorMs + windowDays * DAY_MS)}`)
	if (slug !== undefined && slug !== "") parts.unshift(`in:#${slug}`)
	return parts.join(" ")
}

/**
 * The seq of the first message of `conv` written on or after `day`, or 0 when there is none. The
 * pages of one window run newest first, so the answer is the smallest qualifying seq of the whole
 * window rather than the last row of the last page: a reply written later carries a higher seq than
 * a message that stands above it, and the reader asked for the row they would see first.
 */
export async function seqOnOrAfterDay(
	conv: ConversationId,
	slug: string | undefined,
	day: string,
): Promise<number> {
	const floorMs = dayFloorMs(day)
	for (const windowDays of WINDOWS) {
		const q = jumpQuery(floorMs, windowDays, slug)
		let cursor = ""
		let found = 0
		for (let page = 0; page < MAX_PAGES; page++) {
			const answer = await search({ q, mode: "recent", limit: PAGE, cursor })
			for (const hit of answer.results) {
				if (hit.scopeKind !== "conversation" || hit.scopeId !== conv) continue
				if (hit.createdAtMs < floorMs) continue
				if (found === 0 || hit.scopeSeq < found) found = hit.scopeSeq
			}
			if (answer.nextCursor === "") break
			cursor = answer.nextCursor
		}
		if (found !== 0) return found
	}
	return 0
}

/** Where the reader goes when they ask for a seq of this conversation, or "" when it has no address. */
function permalinkOf(conv: ConversationId, seq: number): string {
	const meta = client.api.getState().conversations[conv]?.meta
	return meta === undefined ? "" : (messageHref(meta, seq) ?? "")
}

/**
 * The first seq this reader may see: seq 1 in a conversation they have been in from the start, and
 * the floor they were let in at otherwise (`MemberSelf.visibleFromSeq`) — asking for seq 1 there
 * would land on the divider that says the history above it is not theirs.
 */
function beginningSeq(conv: ConversationId): number {
	const self = chatSlice(client.api.getState()).self[conv]
	return Math.max(1, self?.visibleFromSeq ?? 0)
}

/**
 * The panel. Every row answers the same way — a day becomes a seq, the seq becomes the permalink the
 * conversation is already opened by — so the landing, the flash and the paging are the permalink's
 * and are not written a second time here.
 *
 * The two sentences it can end with are its own and not `useAsyncAction`'s refusal key: "nothing was
 * written then" is a result rather than a failure, and an instance without a search index owes the
 * reader the reason it cannot answer instead of the status of a route they never asked about.
 */
function JumpPanel({
	conv,
	trigger,
	onJumped,
}: {
	conv: ConversationId
	/** The pill this panel hangs from, so Escape can put the focus back on it — or say it is gone. */
	trigger: RefObject<HTMLButtonElement | null>
	onJumped: () => void
}) {
	const [day, setDay] = useState(() => timeAsDay(Date.now()))
	const [note, setNote] = useState("")
	// The one clock of the interface (`core/i18n/useNow.ts`): the day the field may not go past is
	// today, and a panel left open over midnight would otherwise keep yesterday's ceiling.
	const now = useNow()

	/**
	 * Leaves the panel and opens the conversation at that message. The focus goes to the main region
	 * rather than back to the trigger: the pill it was opened from belongs to a day the list is
	 * about to replace, and a `.focus()` on an element that has just been unmounted drops the reader
	 * at the top of the document (`ui/Dialog.tsx` makes the same choice for the same reason).
	 */
	const go = useCallback(
		(seq: number) => {
			const href = permalinkOf(conv, seq)
			if (href === "") return
			onJumped()
			document.getElementById(MAIN_ID)?.focus()
			navigateTo(href)
		},
		[conv, onJumped],
	)

	const { run, busy } = useAsyncAction(async (wanted: string) => {
		setNote("")
		const meta = client.api.getState().conversations[conv]?.meta
		let seq: number
		try {
			seq = await seqOnOrAfterDay(conv, meta?.slug, wanted)
		} catch (err) {
			setNote(searchIsUnavailable(err) ? t("chat.jump.unavailable") : t("chat.jump.failed"))
			return
		}
		if (seq === 0) {
			setNote(t("chat.jump.none"))
			return
		}
		go(seq)
	})

	/**
	 * Escape closes the panel, and where the focus lands is decided here rather than left to the
	 * primitive: the pill is a row of a list that replaces itself — a page arriving, a jump — and the
	 * primitive's "give it back to the trigger" is a `.focus()` on an element that may no longer be in
	 * the document, which drops the reader at the top of it. The key is only read here; closing the
	 * panel stays the layer's own, which is what makes the two agree.
	 */
	function onEscape(event: React.KeyboardEvent): void {
		if (event.key !== "Escape") return
		const pill = trigger.current
		if (pill !== null && pill.isConnected) pill.focus()
		else document.getElementById(MAIN_ID)?.focus()
	}

	return (
		// Escape belongs to the whole panel and not to whichever control has the focus, so it is
		// captured on the way down — the way `ConversationView` answers the Escape that clears a
		// selection.
		<section className="tb-jump" onKeyDownCapture={onEscape}>
			{PRESETS.map((preset) => (
				<Button
					key={preset.id}
					className="tb-jump__row"
					label={preset.label(now)}
					disabled={busy}
					onClick={() => void run(timeAsDay(Date.now() - preset.back * DAY_MS))}
				/>
			))}
			<div className="tb-jump__date">
				<Field label={t("chat.jump.dateLabel")}>
					{(field) => (
						// `Field` hands the control its own class, so the native input is already a
						// `.tb-field__control` and nothing here restates it (§6.2, D19).
						<input
							{...field}
							type="date"
							value={day}
							max={timeAsDay(now)}
							onChange={(event) => setDay(event.target.value)}
						/>
					)}
				</Field>
				<Button
					variant="neutral"
					label={t("chat.jump.go")}
					disabled={busy || !isDay(day)}
					onClick={() => void run(day)}
				/>
			</div>
			<Button
				className="tb-jump__row"
				label={t("chat.jump.beginning")}
				disabled={busy}
				onClick={() => go(beginningSeq(conv))}
			/>
			{busy ? <p className="tb-jump__note">{t("chat.jump.searching")}</p> : null}
			<FormAlert message={note} />
		</section>
	)
}

/**
 * One day divider of the timeline, as the door into the rest of its history. The chip is the trigger,
 * so the affordance stands where the date already stands; `<time>` keeps the machine readable stamp
 * it had (A31), and the divider — the rule on either side — is the row's, because the sticky
 * behaviour is the row's and not this control's.
 *
 * The chip is `.tb-chip`: one small round raised object, painted once in `ui/ui.css`, and `list.css`
 * only says where in the divider it stands. It carries no calendar glyph — the words under it are a
 * date already (§7.4).
 */
export function JumpToDatePill({ conv, dayStartMs }: { conv: ConversationId; dayStartMs: number }) {
	// The shared Popover is uncontrolled, so the panel is closed by mounting a fresh one: the key
	// changes and Radix unmounts the open panel with it (the pattern of `MessageRow`'s picker).
	const [panel, setPanel] = useState(0)
	const trigger = useRef<HTMLButtonElement>(null)
	// "Today" stops being today at midnight, and a timeline nobody touches overnight is exactly where
	// that is noticed: the label is read off the interface's own clock rather than the render's.
	const label = formatDay(dayStartMs, useNow())
	return (
		<div className="tb-divider tb-divider--day">
			<Popover
				key={panel}
				align="center"
				label={t("chat.jump.title")}
				trigger={
					<button
						ref={trigger}
						type="button"
						className="tb-chip tb-chip--round tb-jump-day"
						aria-label={t("chat.jump.trigger", { day: label })}
					>
						<time dateTime={isoDateTime(dayStartMs)}>{label}</time>
					</button>
				}
			>
				<JumpPanel conv={conv} trigger={trigger} onJumped={() => setPanel((count) => count + 1)} />
			</Popover>
		</div>
	)
}

/**
 * The chord's door (`chat.jumpToDate`): the panel opens on the pill the reader is looking at, which
 * is the one stuck at the top of the list — the last day row whose top has reached the top edge of
 * the scroller, and the first one below it when none has. A conversation with nothing in it has no
 * day and therefore no date to jump to, and the key then does nothing.
 */
export function openJumpToDate(): void {
	const scroller = document.querySelector<HTMLElement>(".tb-conversation .tb-list")
	if (scroller === null) return
	const edge = scroller.getBoundingClientRect().top
	const pills = [...scroller.querySelectorAll<HTMLElement>(".tb-jump-day")]
	let chosen = pills[0]
	for (const pill of pills) {
		const row = pill.closest("li")
		if (row !== null && row.getBoundingClientRect().top <= edge + 2) chosen = pill
	}
	// Focused first: Radix gives the focus back to the trigger when the panel closes, and a panel
	// opened by a key press must return the reader to where they pressed it.
	chosen?.focus()
	chosen?.click()
}
